import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';

import type {
  CardOnFileRequest,
  CardOnFileResponse,
  DepositConfirmRequest,
  DepositConfirmResponse,
  DepositIntentRequest,
  DepositIntentResponse,
  WaiverElectionRequest,
  WaiverElectionResponse,
} from '@rheora/enrollment-contract';

import {
  postCardOnFile,
  postDepositConfirm,
  postDepositIntent,
  postWaiverElection,
} from './deposit.fetchers';
import { funnelQueryKeys } from './funnel.queries';
import { signQueryKeys } from './sign.queries';

/**
 * TanStack hooks for the E-SF05 deposit step. Pages call THESE, never the
 * fetchers. All three are mutations (POSTs with side effects + idempotency keys).
 *
 * ── ALL FOUR INVALIDATE EVERY READER OF THE DEPOSIT GATE ───────────────────
 * The deposit GATE is server-authoritative (`DepositGate.satisfied`) and each of
 * these moves it: confirm and card-on-file settle it (DEPOSIT_COLLECTED), a waiver
 * election retires it (DEPOSIT_WAIVED / DEPOSIT_INSTALLMENT_SCHEDULED). ⚠️ Serving
 * a STALE gate is not a cosmetic problem: a refresh showing "deposit still due"
 * after a paid deposit invites the customer to pay twice, and the SPA must never
 * recompute `satisfied` itself to work around it.
 */

/**
 * Invalidate every cached answer that depends on the deposit gate.
 *
 * ⚠️ TWO KEYS, AND THE SECOND ONE IS THE LOAD-BEARING ONE. These mutations used to
 * invalidate the funnel snapshot alone, which could never have refreshed the sign
 * step: `FunnelProvider` seeds from that snapshot ONCE and never re-reads it, so
 * nothing on screen observes it. The surface the customer is looking at renders
 * from `GET /bff/sign/context`.
 *
 * Measured on 2026-08-26: a §25.478 waiver election moved the server to
 * `DEPOSIT_WAIVED / satisfied: true` while the page kept rendering "A deposit is
 * required before you can sign" with the primary action disabled — until a manual
 * reload. The snapshot stays in the list because it is what a NEW tab rehydrates
 * from; the sign context is what the CURRENT tab draws from. Both are stale after a
 * deposit write, so both go.
 *
 * The sign key is invalidated by PREFIX (`signQueryKeys.all()`) rather than by
 * candidate: this module holds no candidate id, and inventing a way to pass one
 * here would put the deposit step in the business of knowing how sign queries are
 * keyed. There is at most one sign context cached per session, so the prefix costs
 * nothing.
 */
function invalidateDepositGateReaders(queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.invalidateQueries({ queryKey: funnelQueryKeys.snapshot() });
  void queryClient.invalidateQueries({ queryKey: signQueryKeys.all() });
}

export function useDepositIntentMutation(): UseMutationResult<
  DepositIntentResponse,
  Error,
  DepositIntentRequest
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postDepositIntent,
    onSuccess: () => invalidateDepositGateReaders(queryClient),
  });
}

export function useDepositConfirmMutation(): UseMutationResult<
  DepositConfirmResponse,
  Error,
  DepositConfirmRequest
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postDepositConfirm,
    onSuccess: () => invalidateDepositGateReaders(queryClient),
  });
}

/**
 * Put a masked card on file (no Stripe). Settles the deposit gate to
 * DEPOSIT_COLLECTED, so — like confirm — it must invalidate the funnel snapshot.
 */
export function useCardOnFileMutation(): UseMutationResult<
  CardOnFileResponse,
  Error,
  CardOnFileRequest
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postCardOnFile,
    onSuccess: () => invalidateDepositGateReaders(queryClient),
  });
}

export function useWaiverElectionMutation(): UseMutationResult<
  WaiverElectionResponse,
  Error,
  WaiverElectionRequest
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postWaiverElection,
    onSuccess: () => invalidateDepositGateReaders(queryClient),
  });
}
